import { useEffect, useState } from 'react';
import { StepCard } from '../StepCard';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import axios from 'axios';
import { Check, X } from 'lucide-react';
import { OnboardingSkeleton } from '../../../Auth/OnboardingSkeleton';
import { SelectGroup } from '../../../components/ui/selectDashboard';
import { cn } from '../../../lib/utils';

export function SchoolingTarget({ onNext, onPrevious, data, onDataChange, isOnboarding, onComplete }) {
  const [formData, setFormData] = useState({
    class: data?.class || '',
    board: data?.board || '',
    targetYear: data?.targetYear || '',
    exams: data?.exams || [],
    partialDropper: data?.partialDropper || false,
  });
  const [classes, setClasses] = useState([]);
  const [boards, setBoards] = useState([]);
  const [targetYears, setTargetYears] = useState([]);
  const [exams, setTargetExams] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [errors, setErrors] = useState({});
  const [selectedClass, setSelectedClass] = useState(null);
  const [tempExams, setTempExams] = useState([]);
  const [isExamSelectOpen, setIsExamSelectOpen] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setIsLoadingData(true);

      const classesResponse = await axios.get(`/api/user/profile/classes`);
      const classesData = classesResponse.data?.data || [];
      setClasses(classesData);

      const boardsResponse = await axios.get(`/api/user/profile/boards`);
      const boardsData = boardsResponse.data?.data || [];
      setBoards(boardsData);

      const yearsResponse = await axios.get(`/api/user/profile/target-years`);
      const yearsData = yearsResponse.data?.data || [];
      setTargetYears(yearsData);

      const examsResponse = await axios.get(`/api/user/profile/allExams`);
      const examsData = examsResponse.data?.data?.exams || [];
      setTargetExams(examsData);

      const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
      if (userProfile.class) {
        const existingClass = classesData.find((cls) => cls._id === userProfile.class?._id);
        if (existingClass) {
          setSelectedClass(existingClass);
          setFormData((prev) => ({
            ...prev,
            class: existingClass._id,
            partialDropper: userProfile.partialDropper || false,
            exams: userProfile.targetExams?.map((exam) => exam._id || exam) || [],
          }));
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          class: data?.class || '',
          exams: data?.targetExams || [],
          partialDropper: data?.partialDropper || false,
        }));
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setErrors({ fetch: 'Failed to load data. Please refresh the page.' });
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Auto-apply tempExams if the select is open and user hasn't clicked Apply
    const examsToSubmit = isExamSelectOpen ? tempExams : formData.exams;

    // Close the dropdown if it's open
    if (isExamSelectOpen) {
      setIsExamSelectOpen(false);
    }

    if (!formData.class || !examsToSubmit.length) {
      setErrors({ form: 'Please fill all required fields' });
      return;
    }

    setIsLoading(true);
    try {
      const updateData = {
        class: formData.class,
        exams: examsToSubmit,
        partialDropper: formData.partialDropper,
        isDropper: selectedClass?.isDropper || false,
        ...(isOnboarding && { isOnboardingDone: true }),
      };
      const response = await axios.patch(`/api/user/profile/`, updateData);

      if (response.status === 200) {
        localStorage.setItem('userProfile', JSON.stringify(response.data.data.user));
        onDataChange(response.data.data.user);
        onComplete();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ submit: 'Failed to save data. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === 'class') {
      const selected = classes.find((cls) => cls._id === value);
      setSelectedClass(selected);
      setFormData((prev) => ({ ...prev, exams: [] }));
    }

    if (errors.form) {
      setErrors((prev) => ({ ...prev, form: '' }));
    }
  };

  const handleTempExamSelect = (examId) => {
    if (examId === 'all') {
      const allExamIds = exams.filter((exam) => exam._id !== 'all').map((exam) => exam._id);
      const allSelected = tempExams.length === allExamIds.length;

      if (allSelected) {
        setTempExams([]);
      } else {
        setTempExams(allExamIds);
      }
    } else {
      setTempExams((prev) => (prev.includes(examId) ? prev.filter((id) => id !== examId) : [...prev, examId]));
    }
  };

  const handleClearExams = () => {
    setTempExams([]);
  };

  const handleApplyExams = () => {
    setFormData((prev) => ({ ...prev, exams: tempExams }));
    setIsExamSelectOpen(false);
  };

  const getSelectedExamsDisplay = () => {
    if (formData.exams.length === 0) return 'Select Target Exams';
    const selectedExamTitles = formData.exams
      .map((examId) => {
        const exam = exams.find((e) => e._id === examId);
        return exam ? exam.title : '';
      })
      .filter(Boolean);
    return selectedExamTitles.join(', ');
  };

  if (isLoadingData) {
    return <OnboardingSkeleton />;
  }

  return (
    <StepCard>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[var(--q3-neutral-default)] mb-2">
            Add Your Preparation Details
          </h2>
          <p className="text-[var(--q3-neutral-light)] text-sm sm:text-base">
            This will help us in improving your experience.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            {/* Class Select */}
            <Select value={formData.class} onValueChange={(value) => handleSelectChange('class', value)}>
              <SelectTrigger className="border-[var(--q3-stroke-normal)] h-[40px] w-full rounded-full flex justify-between items-center px-4 focus:outline-none focus:ring-0">
                {formData.class ? (
                  <div className="flex items-center gap-1">
                    <span className="shrink-0 text-[var(--q3-neutral-light)]">Class :</span>
                    <SelectValue placeholder="Select Your Class" />
                  </div>
                ) : (
                  <SelectValue placeholder="Select Your Class" />
                )}
              </SelectTrigger>
              <SelectContent className="rounded-3xl">
                {classes.map((cls) => (
                  <SelectItem
                    className="cursor-pointer rounded-3xl hover:bg-[var(--q3-surface-dimmer)]"
                    key={cls._id}
                    value={cls._id}
                  >
                    <span className="text-[var(--q3-neutral-default)]">{cls.title}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {exams.length > 0 && (
              <div className="space-y-2">
                <Select
                  value=""
                  onValueChange={handleTempExamSelect}
                  open={isExamSelectOpen}
                  onOpenChange={(open) => {
                    if (open) {
                      setTempExams(formData.exams);
                      setIsExamSelectOpen(true);
                    } else if (!open && !isExamSelectOpen) {
                      setIsExamSelectOpen(false);
                    }
                  }}
                >
                  <SelectTrigger
                    className={cn(
                      'border-[var(--q3-stroke-normal)] h-[40px] w-full rounded-full flex justify-between items-center px-4 focus:outline-none focus:ring-0',
                      isExamSelectOpen &&
                        'border-[var(--q3-accent-normal)] bg-[var(--q3-accent-light)] text-[var(--q3-accent-normal)] border-2',
                      !isExamSelectOpen && formData.exams.length > 0 && ' bg-[var(--q3-accent-light)] border-2',
                    )}
                  >
                    {formData.exams.length > 0 ? (
                      <div className="flex items-center gap-1 w-full overflow-hidden">
                        <span className="shrink-0 text-muted-foreground">Exams :</span>
                        <div className="flex-1 truncate text-left">
                          <span className="text-sm text-[var(--q3-neutral-default)] ">
                            {(() => {
                              const selectedExamTitles = formData.exams
                                .map((examId) => {
                                  const exam = exams.find((e) => e._id === examId);
                                  return exam ? exam.title : '';
                                })
                                .filter(Boolean);
                              const joinedTitles = selectedExamTitles.join(', ');
                              return joinedTitles.length > 45 ? joinedTitles.slice(0, 45) + '...' : joinedTitles;
                            })()}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <SelectValue placeholder={getSelectedExamsDisplay()} />
                    )}
                  </SelectTrigger>
                  <SelectContent
                    className="rounded-3xl"
                    onPointerDownOutside={() => {
                      setIsExamSelectOpen(false);
                      setFormData((prev) => ({ ...prev, exams: tempExams }));
                    }}
                  >
                    <div className="flex justify-end gap-2 border-b">
                      <Button
                        variant="link"
                        className="text-xs text-[var(--q3-danger-normal)]"
                        onClick={handleClearExams}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="link"
                        className="text-xs text-[var(--q3-accent-normal)]"
                        onClick={handleApplyExams}
                      >
                        Apply
                      </Button>
                    </div>
                    {exams.map((exam) => (
                      <SelectItem
                        className={cn(
                          'cursor-pointer rounded-3xl mb-2 hover:bg-[var(--q3-surface-dimmer)] py-2',
                          (exam._id === 'all'
                            ? tempExams.length === exams.filter((e) => e._id !== 'all').length
                            : tempExams.includes(exam._id)) && 'text-[var(--q3-accent-normal)]',
                        )}
                        key={exam._id}
                        value={exam._id}
                      >
                        <div className="flex items-center gap-2 w-full">
                          <div
                            className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                              (
                                exam._id === 'all'
                                  ? tempExams.length === exams.filter((e) => e._id !== 'all').length
                                  : tempExams.includes(exam._id)
                              )
                                ? 'bg-[var(--q3-accent-normal)] border-[var(--q3-accent-normal)]'
                                : ''
                            }`}
                          >
                            {(exam._id === 'all'
                              ? tempExams.length === exams.filter((e) => e._id !== 'all').length
                              : tempExams.includes(exam._id)) && (
                              <Check className="w-3 h-3 text-[var(--q3-neutral-default_inverted)] " />
                            )}
                          </div>

                          <div className="flex items-center gap-2 flex-1">
                            {exam.icon && (
                              <img src={exam.icon || '/placeholder.svg'} alt={exam.title} className="w-4 h-4" />
                            )}
                            <span>{exam.title}</span>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedClass?.examCategory?._id && exams.length === 0 && (
              <div className="text-center py-4">
                <p className="text-[var(--q3-neutral-light)] text-sm">Loading target exams...</p>
              </div>
            )}
          </div>

          {errors.fetch && <div className="text-[var(--q3-danger-normal)] text-sm text-center">{errors.fetch}</div>}
          {errors.form && <div className="text-[var(--q3-danger-normal)] text-sm text-center">{errors.form}</div>}
          {errors.submit && <div className="text-[var(--q3-danger-normal)] text-sm text-center">{errors.submit}</div>}

          <div className="flex flex-col sm:flex-row pt-4 gap-4 sm:gap-x-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onPrevious}
              className="w-full sm:flex-shrink-0 sm:flex-grow-0 sm:basis-1/3 px-4 sm:px-8 rounded-full bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-default)]/90 text-[var(--q3-neutral-default)] disabled:opacity-50 border border-[var(--q3-stroke-normal)]"
              disabled={isLoading}
            >
              Previous
            </Button>
            <Button
              type="submit"
              className="w-full sm:flex-shrink-0 sm:flex-grow sm:basis-2/3 bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)] disabled:opacity-50 px-4 sm:px-8 rounded-full"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Complete Setup'}
            </Button>
          </div>
        </form>
      </div>
    </StepCard>
  );
}
